import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Collapse,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    Menu,
    MenuItem,
    Paper,
    Select,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import HelpOverlay from '../common/HelpOverlay';
// Icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ClearIcon from '@mui/icons-material/Clear';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import FolderIcon from '@mui/icons-material/Folder';

import MergeTypeIcon from '@mui/icons-material/MergeType';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import VideoFileIcon from '@mui/icons-material/VideoFile';

import { alpha } from '@mui/material/styles';
import { userViewHelpItems } from '../../utils/helpItems';
import FolderBreadcrumbs from './ModernBreadcrumbs';
// Components
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import TranslateIcon from '@mui/icons-material/Translate';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useTheme } from '@mui/material/styles';
import { useAssignmentContext } from '../../contexts/AssignmentContext';
import FileSearchService from '../../services/FileSearchService';
import { isAdmin, isDubber } from '../../utils/authUtils';
import { getProcessStageColor } from '../../utils/processStageUtils';
import EmptyState from '../common/EmptyState';
import { PageHeader } from '../common/PageHeader';
import { SORT_OPTIONS } from '../common/SortSelector';
import BulkDownloadDialog from '../dialogs/BulkDownloadDialog';
import MergeSrtDialog from '../dialogs/MergeSrtDialog';
import SrtSearchDialog from '../dialogs/SrtSearchDialog';
import CreateBookDialog from '../idml/CreateBookDialog';
import StartTrackingDialog from '../tasks/StartTrackingDialog';
import useTaskSummary from '../tasks/useTaskSummary';
import CreateTextFileDialog from '../textfiles/CreateTextFileDialog';
import FileGridItem from './FileGridItem';
import ListView from './ListView';

const ModernFolderView = ({
    folders,
    videoFiles,
    textFiles,
    audioFiles,
    bookFiles = [],
    currentPath,
    onFolderClick,
    onVideoFileClick,
    onTextFileClick,
    onAudioFileClick,
    onBookFileClick,
    onCreateFolder,
    onRename,
    onDelete,
    onBulkDelete,
    onCreateVideo,
    onSplitVideo,
    onCreateAudio,
    videoExperience,
    onMenuOpen,
    onRefresh,
    onTranslateTextFiles,
    onDownloadTextFilesByLanguage,
    // Add themeVariant prop with default value
    themeVariant = 'minimalist',
    darkMode,
    toggleDarkMode,
    onSrtSearchResultClick,
}) => {
    // Add state for assignment dialog
    const [textFileDialogOpen, setTextFileDialogOpen] = useState(false);
    const [bookDialogOpen, setBookDialogOpen] = useState(false);
    const [bulkDownloadDialogOpen, setBulkDownloadDialogOpen] = useState(false);
    const [mergeSrtDialogOpen, setMergeSrtDialogOpen] = useState(false);
    const [srtSearchDialogOpen, setSrtSearchDialogOpen] = useState(false);

    // Bulk select state (admin only)
    const [isSelectMode, setIsSelectMode] = useState(false);
    // Map of fileKey → file object for selected files
    const [selectedFiles, setSelectedFiles] = useState(new Map());

    // Selection is shared by bulk delete (admins) and Start tracking (PMs)
    const [selectPurpose, setSelectPurpose] = useState('delete');
    const [startTrackingOpen, setStartTrackingOpen] = useState(false);
    const taskSummary = useTaskSummary();

    const handleToggleSelectMode = (purpose = 'delete') => {
        setSelectPurpose(purpose);
        setIsSelectMode((prev) => !prev);
        setSelectedFiles(new Map());
    };

    const handleSelectToggle = (fileKey, file) => {
        setSelectedFiles((prev) => {
            const next = new Map(prev);
            if (next.has(fileKey)) {
                next.delete(fileKey);
            } else {
                next.set(fileKey, file);
            }
            return next;
        });
    };

    const handleBulkDeleteClick = () => {
        if (onBulkDelete && selectedFiles.size > 0) {
            onBulkDelete(Array.from(selectedFiles.values()));
            setSelectedFiles(new Map());
            setIsSelectMode(false);
        }
    };

    // Other existing states...
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [searchResults, setSearchResults] = useState(null);
    const [sortBy, setSortBy] = useState(() => {
        const savedSort = localStorage.getItem('folderSortBy');
        return savedSort || SORT_OPTIONS.NAME_ASC;
    });
    const [viewMode, setViewMode] = useState('normal'); // 'normal', 'assigned', 'recent'
    const [assignedVideos, setAssignedVideos] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [advancedMenuAnchorEl, setAdvancedMenuAnchorEl] = useState(null);
    const [newMenuAnchorEl, setNewMenuAnchorEl] = useState(null);
    const [searchCurrentFolderOnly, setSearchCurrentFolderOnly] = useState(true);
    const [advancedSearchOpen, setAdvancedSearchOpen] = useState(false);
    const [openAssignments, setOpenAssignments] = useState([]);
    const [searchFilters, setSearchFilters] = useState({
        languages: [],
        processStages: [],
    });
    const [availableLanguages, setAvailableLanguages] = useState([
        'English',
        'Gujarati',
        'Hindi',
        'Spanish',
        'French',
        'German',
        'Russian',
        'Mandarin',
        'Portuguese',
        'Korean',
        'Kannada',
        'Telugu',
        'Tamil',
        'Bengali',
        'Marathi',
        'Arabic',
        'Malayalam',
        'Italian',
        'Dutch',
        'Polish',
        'Turkish',
        'Greek',
        'Japanese',
        'Thai',
        'Vietnamese',
        'Indonesian',
        'Malay',
        'Burmese',
        'Persian',
        'Hebrew',
        'Swahili',
        'Hausa',
        'Urdu',
        'Punjabi',
        'Sinhala',
        'Nepali',
        'Tagalog',
        'Khmer',
        'Lao',
        'Swedish',
        'Norwegian',
        'Danish',
        'Finnish',
        'Hungarian',
        'Czech',
        'Romanian',
        'Yoruba',
        'Igbo',
        'Amharic',
        'Somali',
        'Xhosa',
        'Zulu',
        'Quechua',
        'Catalan',
        'Uzbek',
        'Kazakh',
        'Mongolian',
        'Tibetan',
        'Armenian',
        'Georgian',
    ]);
    const [availableProcessStages, setAvailableProcessStages] = useState([
        { value: 'LEVEL0', label: 'AI Generation Complete' },
        { value: 'LEVEL1', label: 'Level 1 Complete' },
        { value: 'LEVEL2', label: 'Level 2 Complete' },
        { value: 'PUBLISHED', label: 'Published' },
    ]);
    const theme = useTheme();
    const canCreateDubbingAudio = isAdmin() || isDubber();

    // Use the assignment context
    const { loadingAssignments, fetchAssignedVideos } = useAssignmentContext();

    const normalizedPath = useMemo(() => {
        let path = currentPath || '/';
        if (!path.startsWith('/')) {
            path = '/' + path;
        }
        return path.replace(/\/+/g, '/');
    }, [currentPath]);

    // Memoize the refresh handler
    const handleRefresh = useCallback(() => {
        onRefresh();
    }, [onRefresh]);

    // Memoize the search handler
    const performSearch = useCallback(
        async (query) => {
            // Don't search if there are no search criteria
            if (!query.trim() && !searchFilters.languages.length && !searchFilters.processStages.length) {
                setSearchResults(null);
                return;
            }

            try {
                setIsSearching(true);

                // Get auth data from localStorage
                const authData = localStorage.getItem('authToken');
                const userData = JSON.parse(localStorage.getItem('authUser') || '{}');

                console.log('Performing search with criteria:', {
                    query: query.trim(),
                    languages: searchFilters.languages,
                    processStages: searchFilters.processStages,
                    currentFolder: searchCurrentFolderOnly ? normalizedPath : null,
                });

                const results = await FileSearchService.advancedSearch({
                    query: query.trim(),
                    email: userData.email,
                    auth: authData ? authData.replace(/"/g, '') : userData.auth,
                    languages: searchFilters.languages,
                    processStages: searchFilters.processStages,
                    currentFolder: searchCurrentFolderOnly ? normalizedPath : null,
                });

                console.log('Search results received:', results);

                // Ensure we have valid results
                setSearchResults({
                    folders: Array.isArray(results.folders) ? results.folders : [],
                    videoFiles: Array.isArray(results.videoFiles) ? results.videoFiles : [],
                    textFiles: Array.isArray(results.textFiles) ? results.textFiles : [],
                    audioFiles: Array.isArray(results.audioFiles) ? results.audioFiles : [],
                });
            } catch (error) {
                console.error('Search error:', error);
                setSearchResults({
                    folders: [],
                    files: [],
                    error: error.response?.data?.message || 'Unable to perform search. Please try again later.',
                });
            } finally {
                setIsSearching(false);
            }
        },
        [searchFilters, searchCurrentFolderOnly, normalizedPath],
    );

    // Memoize the clear search handler
    const clearSearch = useCallback(() => {
        console.log('Clearing search term');
        setSearchTerm('');
        setSearchResults(null);
    }, []);

    // Memoize the clear filters handler
    const clearFilters = useCallback(() => {
        console.log('Clearing search filters');
        setSearchFilters({
            languages: [],
            processStages: [],
        });
    }, []);

    // Memoize the clear all search handler
    const clearAllSearch = useCallback(() => {
        console.log('Clearing all search criteria');
        setSearchTerm('');
        clearFilters();
        setSearchResults(null);
    }, [clearFilters]);

    // Memoize the toggle advanced search handler
    const toggleAdvancedSearch = useCallback(() => {
        setAdvancedSearchOpen((prev) => !prev);
    }, []);

    // Memoize the fetch assigned videos handler
    const fetchAndSetAssignedVideos = useCallback(async () => {
        const result = await fetchAssignedVideos();
        if (result && result.videos) {
            setAssignedVideos(result.videos);
        } else {
            console.error('No assigned videos found or error fetching assigned videos');
            setAssignedVideos([]);
        }
        if (result && result.assignmentData) {
            // filter the ones that are not COMPLETED
            const openAssignments = result.assignmentData.filter((a) => a.status !== 'COMPLETED');
            setOpenAssignments(openAssignments);
        }
    }, [fetchAssignedVideos]);

    useEffect(() => {
        // Clear search timeout on component unmount or when dependencies change
        let searchTimer = null;

        // Only set up the timer if we have search criteria
        if (
            searchTerm.trim().length >= 2 ||
            searchFilters.languages.length > 0 ||
            searchFilters.processStages.length > 0
        ) {
            searchTimer = setTimeout(() => {
                performSearch(searchTerm);
            }, 1000);
        } else if (searchTerm === '') {
            // Reset search results when search is cleared
            setSearchResults(null);
        }

        // Cleanup function to clear timer when component unmounts or dependencies change
        return () => {
            if (searchTimer) {
                clearTimeout(searchTimer);
            }
        };
    }, [searchTerm, searchFilters, performSearch]);

    useEffect(() => {
        if (viewMode === 'assigned') {
            fetchAndSetAssignedVideos();
        }
    }, [viewMode]);

    useEffect(() => {
        // Fetch assigned videos on component mount
        fetchAndSetAssignedVideos();
    }, []); // Empty dependency array means this runs once on mount

    const handleSortChange = (newSortBy) => {
        setSortBy(newSortBy);
    };


    const handleSearchKeyDown = (e) => {
        if (e.key === 'Enter') {
            if (
                searchTerm.trim().length >= 2 ||
                searchFilters.languages.length > 0 ||
                searchFilters.processStages.length > 0
            ) {
                console.log('Search triggered by Enter key');
                e.preventDefault();
                performSearch(searchTerm);
            }
        } else if (e.key === 'Escape') {
            console.log('Search cleared by Escape key');
            e.preventDefault();
            clearSearch();
        }
    };

    const onCreateText = (path) => {
        setTextFileDialogOpen(true);
    };

    const handleTextFileCreated = (newFile) => {
        // Refresh the folder view
        if (onRefresh) {
            onRefresh();
        }
    };

    const onCreateBook = (path) => {
        setBookDialogOpen(true);
    };

    const handleBookFileCreated = (newFile) => {
        // Refresh the folder view
        if (onRefresh) {
            onRefresh();
        }
        // Open the new book immediately so the editor's poll loop can pick up
        // PARSED status and chain into auto-translation (see IdmlBookEditor).
        if (newFile?.id && onBookFileClick) {
            onBookFileClick({ data: newFile.id });
        }
    };

    const handleBulkDownload = () => {
        setBulkDownloadDialogOpen(true);
    };

    const handleMergeSrt = () => {
        setMergeSrtDialogOpen(true);
    };

    const handleAdvancedMenuOpen = (event) => {
        setAdvancedMenuAnchorEl(event.currentTarget);
    };

    const handleAdvancedMenuClose = () => {
        setAdvancedMenuAnchorEl(null);
    };

    const handleAdvancedMenuAction = (action) => {
        handleAdvancedMenuClose();
        if (action === 'bulkDownload') {
            handleBulkDownload();
        } else if (action === 'mergeSrt') {
            handleMergeSrt();
        } else if (action === 'srtSearch') {
            setSrtSearchDialogOpen(true);
        } else if (action === 'translateTextFiles') {
            onTranslateTextFiles && onTranslateTextFiles();
        } else if (action === 'downloadByLanguage') {
            onDownloadTextFilesByLanguage && onDownloadTextFilesByLanguage();
        }
    };

    const hasActiveFilters = searchFilters.languages.length > 0 || searchFilters.processStages.length > 0;

    const getActionButtonSx = useCallback(
        (paletteKey) => ({
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: '12px',
            border: '1px solid',
            borderColor: (theme) => alpha(theme.palette[paletteKey].main, 0.35),
            backgroundColor: (theme) => alpha(theme.palette[paletteKey].main, 0.1),
            color: (theme) => theme.palette[paletteKey].dark,
            '& .MuiButton-startIcon': {
                color: `${paletteKey}.main`,
            },
            '&:hover': {
                borderColor: (theme) => alpha(theme.palette[paletteKey].main, 0.6),
                backgroundColor: (theme) => alpha(theme.palette[paletteKey].main, 0.18),
            },
            '&.Mui-disabled': {
                borderColor: 'action.disabledBackground',
                backgroundColor: 'action.hover',
                color: 'text.disabled',
            },
        }),
        [],
    );

    const getSearchDescription = () => {
        const parts = [];

        if (searchTerm) {
            parts.push(`name containing "${searchTerm}"`);
        }

        if (searchFilters.languages.length > 0) {
            parts.push(
                `language${searchFilters.languages.length > 1 ? 's' : ''}: ${searchFilters.languages.join(', ')}`,
            );
        }

        if (searchFilters.processStages.length > 0) {
            const stageLabels = searchFilters.processStages.map(
                (stage) => availableProcessStages.find((s) => s.value === stage)?.label || stage,
            );
            parts.push(`stage${searchFilters.processStages.length > 1 ? 's' : ''}: ${stageLabels.join(', ')}`);
        }

        return parts.join(', ');
    };

    // Create sorted versions of the folders and files
    const sortedData = useMemo(() => {
        // Determine which data to sort (search results or regular folders/files)
        const foldersToSort = searchResults ? searchResults.folders : folders;
        const videoFilesToSort = searchResults ? searchResults.videoFiles : videoFiles;
        const textFilesToSort = searchResults ? searchResults.textFiles : textFiles;
        const audioFilesToSort = searchResults ? searchResults.audioFiles : audioFiles;
        const bookFilesToSort = searchResults ? searchResults.bookFiles : bookFiles;

        // Clone arrays to avoid mutating props
        const sortedFolders = foldersToSort ? [...foldersToSort] : [];
        const sortedVideoFiles = videoFilesToSort ? [...videoFilesToSort] : [];
        const sortedTextFiles = textFilesToSort ? [...textFilesToSort] : [];
        const sortedAudioFiles = audioFilesToSort ? [...audioFilesToSort] : [];
        const sortedBookFiles = bookFilesToSort ? [...bookFilesToSort] : [];

        // Sort folders
        sortedFolders.sort((a, b) => {
            switch (sortBy) {
                case SORT_OPTIONS.NAME_ASC:
                    return a.name.localeCompare(b.name);
                case SORT_OPTIONS.NAME_DESC:
                    return b.name.localeCompare(a.name);
                case SORT_OPTIONS.DATE_OLDEST:
                    return new Date(a.updatedAt) - new Date(b.updatedAt);
                case SORT_OPTIONS.DATE_NEWEST:
                    return new Date(b.updatedAt) - new Date(a.updatedAt);
                default:
                    return 0;
            }
        });

        // Sort files
        sortedVideoFiles.sort((a, b) => {
            switch (sortBy) {
                case SORT_OPTIONS.NAME_ASC:
                    return (a.videoName || '').localeCompare(b.videoName || '');
                case SORT_OPTIONS.NAME_DESC:
                    return (b.videoName || '').localeCompare(a.videoName || '');
                case SORT_OPTIONS.DATE_OLDEST:
                    return new Date(a.updatedAt) - new Date(b.updatedAt);
                case SORT_OPTIONS.DATE_NEWEST:
                    return new Date(b.updatedAt) - new Date(a.updatedAt);
                default:
                    return 0;
            }
        });

        sortedTextFiles.sort((a, b) => {
            switch (sortBy) {
                case SORT_OPTIONS.NAME_ASC:
                    return (a.videoName || '').localeCompare(b.videoName || '');
                case SORT_OPTIONS.NAME_DESC:
                    return (b.videoName || '').localeCompare(a.videoName || '');
                case SORT_OPTIONS.DATE_OLDEST:
                    return new Date(a.updatedAt) - new Date(b.updatedAt);
                case SORT_OPTIONS.DATE_NEWEST:
                    return new Date(b.updatedAt) - new Date(a.updatedAt);
                default:
                    return 0;
            }
        });

        sortedAudioFiles.sort((a, b) => {
            switch (sortBy) {
                case SORT_OPTIONS.NAME_ASC:
                    return (a.name || '').localeCompare(b.name || '');
                case SORT_OPTIONS.NAME_DESC:
                    return (b.name || '').localeCompare(a.name || '');
                case SORT_OPTIONS.DATE_OLDEST:
                    return new Date(a.updatedAt) - new Date(b.updatedAt);
                case SORT_OPTIONS.DATE_NEWEST:
                    return new Date(b.updatedAt) - new Date(a.updatedAt);
                default:
                    return 0;
            }
        });

        sortedBookFiles.sort((a, b) => {
            switch (sortBy) {
                case SORT_OPTIONS.NAME_ASC:
                    return (a.name || '').localeCompare(b.name || '');
                case SORT_OPTIONS.NAME_DESC:
                    return (b.name || '').localeCompare(a.name || '');
                case SORT_OPTIONS.DATE_OLDEST:
                    return new Date(a.updatedAt) - new Date(b.updatedAt);
                case SORT_OPTIONS.DATE_NEWEST:
                    return new Date(b.updatedAt) - new Date(a.updatedAt);
                default:
                    return 0;
            }
        });

        return { folders: sortedFolders, videoFiles: sortedVideoFiles, textFiles: sortedTextFiles, audioFiles: sortedAudioFiles, bookFiles: sortedBookFiles };
    }, [folders, videoFiles, textFiles, audioFiles, bookFiles, searchResults, sortBy]);

    useEffect(() => {
        localStorage.setItem('folderSortBy', sortBy);
    }, [sortBy]);

    const handleMenuItemClick = (action) => {
        setAnchorEl(null);
        if (typeof action === 'function') {
            action();
        }
    };

    return (
        <Box
            sx={{
                width: '100%',
                backgroundColor: darkMode ? 'background.default' : '#f5f5f5',
                minHeight: '100vh',
            }}
        >
            <Paper
                sx={{
                    borderRadius: 0,
                    padding: 0,
                }}
            >
                <HelpOverlay
                    title="List View Guide"
                    buttonId="list-view-help-button"
                    helpItems={userViewHelpItems ? userViewHelpItems : []}
                    showButton={true}
                />
                <PageHeader
                    darkMode={darkMode}
                    toggleDarkMode={toggleDarkMode}
                    showDarkMode={true}
                    themeVariant={themeVariant}
                />

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        padding: 1,
                        mb: 3,
                    }}
                >
                    <Box className="breadcrumbs" sx={{ display: 'flex', alignItems: 'center' }}>
                        {!searchResults ? (
                            <FolderBreadcrumbs currentPath={normalizedPath} onNavigate={onFolderClick} />
                        ) : (
                            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                                Search Results: {getSearchDescription()}
                            </Typography>
                        )}
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 2,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            flexGrow: 1,
                            mr: { xs: 0, sm: 2 },
                            mb: { xs: 2, sm: 0 },
                            width: { xs: '100%', sm: 'auto' },
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                width: '100%',
                            }}
                        >
                            <Box sx={{ display: 'flex', width: '100%', gap: 1, alignItems: 'center' }}>
                                <TextField
                                    className="search-field"
                                    size="small"
                                    placeholder={
                                        searchCurrentFolderOnly
                                            ? `Search in ${normalizedPath}...`
                                            : 'Search all files...'
                                    }
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onKeyDown={handleSearchKeyDown}
                                    fullWidth
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                {isSearching ? (
                                                    <CircularProgress size={20} />
                                                ) : (
                                                    <SearchIcon fontSize="small" />
                                                )}
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                {isSearching ? (
                                                    <CircularProgress size={20} />
                                                ) : (
                                                    <>
                                                        {(searchTerm || hasActiveFilters) && (
                                                            <IconButton
                                                                size="small"
                                                                onClick={clearAllSearch}
                                                                aria-label="clear search"
                                                            >
                                                                <ClearIcon fontSize="small" />
                                                            </IconButton>
                                                        )}

                                                        {searchTerm.trim().length >= 2 && (
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => performSearch(searchTerm)}
                                                                aria-label="search"
                                                            >
                                                                <SearchIcon fontSize="small" color="primary" />
                                                            </IconButton>
                                                        )}

                                                        <Tooltip
                                                            title={
                                                                searchCurrentFolderOnly
                                                                    ? 'Searching this folder only'
                                                                    : 'Searching all folders'
                                                            }
                                                        >
                                                            <Box
                                                                onClick={() =>
                                                                    setSearchCurrentFolderOnly(!searchCurrentFolderOnly)
                                                                }
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    ml: 1,
                                                                    cursor: 'pointer',
                                                                    borderLeft: '1px solid',
                                                                    borderColor: 'divider',
                                                                    pl: 1,
                                                                    height: '24px',
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="caption"
                                                                    color="text.secondary"
                                                                    sx={{ mr: 0.5 }}
                                                                >
                                                                    {searchCurrentFolderOnly
                                                                        ? 'This folder'
                                                                        : 'All folders'}
                                                                </Typography>

                                                                <Box
                                                                    sx={{
                                                                        width: '36px',
                                                                        height: '18px',
                                                                        borderRadius: '9px',
                                                                        position: 'relative',
                                                                        backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        padding: '2px',
                                                                        transition: 'background-color 0.3s ease',
                                                                    }}
                                                                >
                                                                    <Box
                                                                        sx={{
                                                                            position: 'absolute',
                                                                            left: searchCurrentFolderOnly
                                                                                ? '2px'
                                                                                : 'calc(100% - 16px)',
                                                                            width: '14px',
                                                                            height: '14px',
                                                                            borderRadius: '50%',
                                                                            backgroundColor: searchCurrentFolderOnly
                                                                                ? 'primary.main'
                                                                                : 'text.secondary',
                                                                            transition:
                                                                                'left 0.3s ease, background-color 0.3s ease',
                                                                        }}
                                                                    />
                                                                </Box>
                                                            </Box>
                                                        </Tooltip>
                                                    </>
                                                )}
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <Tooltip title="Advanced Search">
                                    <span>
                                        <Button
                                            size="small"
                                            variant={advancedSearchOpen ? 'contained' : 'outlined'}
                                            color={hasActiveFilters ? 'secondary' : 'primary'}
                                            onClick={toggleAdvancedSearch}
                                            sx={{
                                                minWidth: 'auto',
                                                height: '40px',
                                                width: '40px',
                                                p: 0,
                                            }}
                                        >
                                            <TuneIcon fontSize="small" />
                                            {hasActiveFilters && (
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: '2px',
                                                        right: '2px',
                                                        width: '8px',
                                                        height: '8px',
                                                        borderRadius: '50%',
                                                        backgroundColor: 'error.main',
                                                    }}
                                                />
                                            )}
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Box>

                            <Collapse in={advancedSearchOpen} timeout="auto" unmountOnExit>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        mt: 1,
                                        p: 2,
                                        borderRadius: 1,
                                        backgroundColor:
                                            theme.palette.mode === 'dark'
                                                ? 'rgba(255, 255, 255, 0.05)'
                                                : 'rgba(0, 0, 0, 0.02)',
                                    }}
                                >
                                    <Typography variant="subtitle2" gutterBottom fontWeight="medium">
                                        Advanced Search Filters
                                    </Typography>

                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 1 }}>
                                        <FormControl sx={{ minWidth: 200, flex: '1 1 200px' }} size="small">
                                            <InputLabel>Languages</InputLabel>
                                            <Select
                                                multiple
                                                value={searchFilters.languages}
                                                onChange={(e) =>
                                                    setSearchFilters({
                                                        ...searchFilters,
                                                        languages: e.target.value,
                                                    })
                                                }
                                                renderValue={(selected) => (
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        {selected.map((value) => (
                                                            <Chip
                                                                key={value}
                                                                label={value}
                                                                size="small"
                                                                color="primary"
                                                                variant="outlined"
                                                            />
                                                        ))}
                                                    </Box>
                                                )}
                                                label="Languages"
                                            >
                                                {availableLanguages.map((language) => (
                                                    <MenuItem key={language} value={language}>
                                                        <Checkbox
                                                            checked={searchFilters.languages.indexOf(language) > -1}
                                                            size="small"
                                                        />
                                                        {language}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        <FormControl sx={{ minWidth: 200, flex: '1 1 200px' }} size="small">
                                            <InputLabel>Completed Process Stage</InputLabel>
                                            <Select
                                                multiple
                                                value={searchFilters.processStages}
                                                onChange={(e) =>
                                                    setSearchFilters({
                                                        ...searchFilters,
                                                        processStages: e.target.value,
                                                    })
                                                }
                                                renderValue={(selected) => (
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                        {selected.map((value) => {
                                                            const stageLabel =
                                                                availableProcessStages.find((s) => s.value === value)
                                                                    ?.label || value;
                                                            return (
                                                                <Chip
                                                                    key={value}
                                                                    label={stageLabel}
                                                                    size="small"
                                                                    sx={{
                                                                        backgroundColor: `${getProcessStageColor(
                                                                            value,
                                                                        )}20`,
                                                                        color: getProcessStageColor(value),
                                                                        borderColor: getProcessStageColor(value),
                                                                    }}
                                                                    variant="outlined"
                                                                />
                                                            );
                                                        })}
                                                    </Box>
                                                )}
                                                label="Process Stage"
                                            >
                                                {availableProcessStages.map((stage) => (
                                                    <MenuItem key={stage.value} value={stage.value}>
                                                        <Checkbox
                                                            checked={
                                                                searchFilters.processStages.indexOf(stage.value) > -1
                                                            }
                                                            size="small"
                                                        />
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1,
                                                            }}
                                                        >
                                                            <Chip
                                                                label={stage.label}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor: `${getProcessStageColor(
                                                                        stage.value,
                                                                    )}20`,
                                                                    color: getProcessStageColor(stage.value),
                                                                    borderColor: getProcessStageColor(stage.value),
                                                                    minWidth: 100,
                                                                }}
                                                                variant="outlined"
                                                            />
                                                        </Box>
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'flex-end',
                                                justifyContent: 'flex-end',
                                                flex: '1 1 100px',
                                            }}
                                        >
                                            <Button
                                                size="small"
                                                onClick={clearFilters}
                                                disabled={!hasActiveFilters}
                                                variant="text"
                                            >
                                                Clear Filters
                                            </Button>

                                            {searchTerm || hasActiveFilters ? (
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={() => performSearch(searchTerm)}
                                                    sx={{ ml: 1 }}
                                                >
                                                    Search
                                                </Button>
                                            ) : null}
                                        </Box>
                                    </Box>
                                </Paper>
                            </Collapse>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1.5,
                            flexWrap: 'wrap',
                            justifyContent: { xs: 'center', sm: 'flex-end' },
                            width: { xs: '100%', sm: 'auto' },
                        }}
                    >
                        {/* Assignment toggle button group - currently disabled */}
                        {false && (
                            <ToggleButtonGroup
                                className="view-assignement-button"
                                value={viewMode}
                                exclusive
                                onChange={(e, newMode) => newMode && setViewMode(newMode)}
                                size="small"
                            >
                                <ToggleButton value="normal" aria-label="Normal view">
                                    <Tooltip title="Normal View">
                                        <FolderIcon fontSize="small" sx={{ display: 'flex' }} />
                                    </Tooltip>
                                </ToggleButton>
                                <ToggleButton value="assigned" aria-label="Assigned to me">
                                    <Tooltip title="Assigned to Me">
                                        <Box sx={{ position: 'relative', display: 'flex' }}>
                                            <AssignmentIcon fontSize="small" sx={{ display: 'flex' }} />
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: -8,
                                                    right: -10,
                                                    backgroundColor: 'error.main',
                                                    color: 'white',
                                                    borderRadius: '50%',
                                                    width: 16,
                                                    height: 16,
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    fontSize: '0.65rem',
                                                    fontWeight: 'bold',
                                                    boxShadow: '0 0 0 2px #fff',
                                                }}
                                            >
                                                {openAssignments.length}
                                            </Box>
                                        </Box>
                                    </Tooltip>
                                </ToggleButton>
                            </ToggleButtonGroup>
                        )}

                        <Tooltip title="Refresh folder">
                            <Button
                                className="refresh-button"
                                color="success"
                                startIcon={<RefreshIcon />}
                                onClick={handleRefresh}
                            >
                                Refresh
                            </Button>
                        </Tooltip>

                        <Tooltip title={currentPath === '/' && !isAdmin() ? 'Only admins can create items in root' : 'Create new item'}>
                            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                <Button
                                    className="new-item-button"
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    endIcon={<KeyboardArrowDownIcon />}
                                    disabled={currentPath === '/' && !isAdmin()}
                                    onClick={(e) => setNewMenuAnchorEl(e.currentTarget)}
                                    sx={{
                                        borderRadius: '24px',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                        fontWeight: 500,
                                        px: 2.5,
                                        py: 0.75,
                                        textTransform: 'none',
                                    }}
                                >
                                    New
                                </Button>
                            </span>
                        </Tooltip>
                        <Menu
                            anchorEl={newMenuAnchorEl}
                            open={Boolean(newMenuAnchorEl)}
                            onClose={() => setNewMenuAnchorEl(null)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            slotProps={{ paper: { sx: { minWidth: 200, mt: 0.5 } } }}
                        >
                            <MenuItem
                                className="create-folder-button"
                                onClick={() => { onCreateFolder(); setNewMenuAnchorEl(null); }}
                            >
                                <ListItemIcon><CreateNewFolderIcon fontSize="small" color="primary" /></ListItemIcon>
                                <ListItemText>New Folder</ListItemText>
                            </MenuItem>
                            <MenuItem
                                className="upload-video-button"
                                onClick={() => { onCreateVideo(normalizedPath); setNewMenuAnchorEl(null); }}
                            >
                                <ListItemIcon><VideoCallIcon fontSize="small" color="secondary" /></ListItemIcon>
                                <ListItemText>New Video</ListItemText>
                            </MenuItem>
                            <MenuItem
                                className="split-video-button"
                                onClick={() => { onSplitVideo(normalizedPath); setNewMenuAnchorEl(null); }}
                            >
                                <ListItemIcon><VideoCallIcon fontSize="small" color="warning" /></ListItemIcon>
                                <ListItemText>New Video (Split then Process)</ListItemText>
                            </MenuItem>
                            {canCreateDubbingAudio && (
                                <MenuItem
                                    className="upload-audio-button"
                                    onClick={() => { onCreateAudio(normalizedPath); setNewMenuAnchorEl(null); }}
                                >
                                    <ListItemIcon><AudiotrackIcon fontSize="small" color="warning" /></ListItemIcon>
                                    <ListItemText>New Dubbing Audio</ListItemText>
                                </MenuItem>
                            )}
                            <MenuItem
                                className="upload-text-button"
                                onClick={() => { onCreateText(normalizedPath); setNewMenuAnchorEl(null); }}
                            >
                                <ListItemIcon><TextSnippetIcon fontSize="small" color="info" /></ListItemIcon>
                                <ListItemText>New Text</ListItemText>
                            </MenuItem>
                            <MenuItem
                                className="upload-book-button"
                                onClick={() => { onCreateBook(normalizedPath); setNewMenuAnchorEl(null); }}
                            >
                                <ListItemIcon><AutoStoriesIcon fontSize="small" color="success" /></ListItemIcon>
                                <ListItemText>New Book</ListItemText>
                            </MenuItem>
                        </Menu>

                        <Tooltip title="Advanced options">
                            <Button
                                color="primary"
                                onClick={handleAdvancedMenuOpen}
                                startIcon={<MoreVertIcon />}
                                sx={{
                                    minWidth: 'auto',
                                    px: 2
                                }}
                                disabled={videoFiles.length === 0 && textFiles.length === 0 && !isAdmin() && !taskSummary?.canManage}
                            >
                                Advanced
                            </Button>
                        </Tooltip>
                        <Menu
                            anchorEl={advancedMenuAnchorEl}
                            open={Boolean(advancedMenuAnchorEl)}
                            onClose={handleAdvancedMenuClose}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            slotProps={{
                                paper: {
                                    sx: { minWidth: 160 }
                                }
                            }}
                        >
                            {/* General section */}
                            {isAdmin() && [
                                <Box
                                    key="general-header"
                                    sx={{
                                        px: 2,
                                        py: 0.5,
                                        fontSize: '0.7rem',
                                        fontWeight: 'medium',
                                        letterSpacing: '0.08em',
                                        textTransform: 'uppercase',
                                        color: 'text.secondary',
                                        borderLeft: '3px solid',
                                        borderColor: 'text.primary',
                                        backgroundColor: (theme) => alpha(theme.palette.action.active, 0.06),
                                        borderRadius: '0 4px 4px 0',
                                        mx: 0.5,
                                        my: 0.3,
                                    }}
                                >
                                    General
                                </Box>,
                                <MenuItem
                                    key="bulkDelete"
                                    onClick={() => {
                                        handleAdvancedMenuClose();
                                        handleToggleSelectMode();
                                    }}
                                    sx={{
                                        py: 1,
                                        px: 2,
                                        fontSize: '0.875rem',
                                        minHeight: 'auto',
                                        color: isSelectMode ? 'error.main' : 'inherit',
                                    }}
                                >
                                    <CheckBoxIcon sx={{ mr: 1, fontSize: '1.1rem', color: isSelectMode ? 'error.main' : 'inherit' }} />
                                    {isSelectMode ? 'Cancel Selection' : 'Bulk Delete Files'}
                                </MenuItem>,
                            ]}
                            {taskSummary?.canManage && !isSelectMode && (
                                <MenuItem
                                    onClick={() => {
                                        handleAdvancedMenuClose();
                                        handleToggleSelectMode('track');
                                    }}
                                    sx={{ py: 1, px: 2, fontSize: '0.875rem', minHeight: 'auto' }}
                                >
                                    <CheckBoxIcon sx={{ mr: 1, fontSize: '1.1rem' }} />
                                    Start tracking files
                                </MenuItem>
                            )}

                            {/* Text Only section */}
                            {textFiles && textFiles.length > 0 && [
                                isAdmin() && <Divider key="text-divider" sx={{ my: 0.5 }} />,
                                <Box
                                    key="text-header"
                                    sx={{
                                        px: 2,
                                        py: 0.5,
                                        fontSize: '0.7rem',
                                        fontWeight: 'medium',
                                        letterSpacing: '0.08em',
                                        textTransform: 'uppercase',
                                        color: 'info.main',
                                        borderLeft: '3px solid',
                                        borderColor: 'info.main',
                                        backgroundColor: (theme) => alpha(theme.palette.info.main, 0.08),
                                        borderRadius: '0 4px 4px 0',
                                        mx: 0.5,
                                        my: 0.3,
                                    }}
                                >
                                    Text Only
                                </Box>,
                                <MenuItem
                                    key="translateTextFiles"
                                    onClick={() => handleAdvancedMenuAction('translateTextFiles')}
                                    sx={{ py: 1, px: 2, fontSize: '0.875rem', minHeight: 'auto' }}
                                >
                                    <TranslateIcon sx={{ mr: 1, fontSize: '1.1rem', color: 'info.main' }} />
                                    Translate Text File
                                </MenuItem>,
                                <MenuItem
                                    key="downloadByLanguage"
                                    onClick={() => handleAdvancedMenuAction('downloadByLanguage')}
                                    sx={{ py: 1, px: 2, fontSize: '0.875rem', minHeight: 'auto' }}
                                >
                                    <DownloadIcon sx={{ mr: 1, fontSize: '1.1rem', color: 'info.main' }} />
                                    Download by Language
                                </MenuItem>,
                            ]}

                            {/* Video Only section */}
                            {videoFiles && videoFiles.length > 0 && [
                                (isAdmin() || (textFiles && textFiles.length > 0)) && <Divider key="video-divider" sx={{ my: 0.5 }} />,
                                <Box
                                    key="video-header"
                                    sx={{
                                        px: 2,
                                        py: 0.5,
                                        fontSize: '0.7rem',
                                        fontWeight: 'medium',
                                        letterSpacing: '0.08em',
                                        textTransform: 'uppercase',
                                        color: 'secondary.main',
                                        borderLeft: '3px solid',
                                        borderColor: 'secondary.main',
                                        backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.08),
                                        borderRadius: '0 4px 4px 0',
                                        mx: 0.5,
                                        my: 0.3,
                                    }}
                                >
                                    Video Only
                                </Box>,
                                <MenuItem
                                    key="bulkDownload"
                                    onClick={() => handleAdvancedMenuAction('bulkDownload')}
                                    sx={{ py: 1, px: 2, fontSize: '0.875rem', minHeight: 'auto' }}
                                >
                                    <DownloadIcon sx={{ mr: 1, fontSize: '1.1rem', color: 'secondary.main' }} />
                                    Bulk Download
                                </MenuItem>,
                                videoFiles.length > 1 && (
                                    <MenuItem
                                        key="mergeSrt"
                                        onClick={() => handleAdvancedMenuAction('mergeSrt')}
                                        sx={{ py: 1, px: 2, fontSize: '0.875rem', minHeight: 'auto' }}
                                    >
                                        <MergeTypeIcon sx={{ mr: 1, fontSize: '1.1rem', color: 'secondary.main' }} />
                                        Merge SRT Files
                                    </MenuItem>
                                ),
                                (
                                    <Tooltip
                                        key="srtSearch"
                                        title={videoFiles.length === 0 ? 'No video files in this folder' : ''}
                                        placement="left"
                                    >
                                        <span>
                                            <MenuItem
                                                onClick={() => handleAdvancedMenuAction('srtSearch')}
                                                disabled={videoFiles.length === 0}
                                                sx={{ py: 1, px: 2, fontSize: '0.875rem', minHeight: 'auto' }}
                                            >
                                                <SearchIcon sx={{ mr: 1, fontSize: '1.1rem', color: 'secondary.main' }} />
                                                Search in SRT Content
                                            </MenuItem>
                                        </span>
                                    </Tooltip>
                                ),
                            ]}
                        </Menu>
                    </Box>
                </Box>
            </Paper>

            {searchResults ? (
                <Box
                    sx={{
                        mb: 4,
                        px: 1,
                    }}
                >
                    {searchResults.error ? (
                        <Typography variant="body2" color="error">
                            {searchResults.error}
                            <Button size="small" sx={{ ml: 2 }} onClick={clearSearch} startIcon={<ArrowBackIcon />}>
                                Back to current folder
                            </Button>
                        </Typography>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            Found {searchResults.folders.length} folders, {searchResults.videoFiles.length} video files
                            , {searchResults.textFiles.length} text files and {searchResults.audioFiles.length} audio files matching "{searchTerm}"
                            {searchCurrentFolderOnly ? (
                                normalizedPath !== '/' ? (
                                    <span> in {normalizedPath}</span>
                                ) : (
                                    <span> in root folder</span>
                                )
                            ) : (
                                <span> across all folders</span>
                            )}
                            <Button size="small" sx={{ ml: 2 }} onClick={clearSearch} startIcon={<ArrowBackIcon />}>
                                Back to current folder
                            </Button>
                        </Typography>
                    )}
                </Box>
            ) : (
                <Box
                    sx={{
                        padding: 1,
                        mb: 4,
                        display: 'flex',
                        gap: 2,
                        px: 1,
                    }}
                >
                    <Chip
                        icon={
                            <FolderIcon
                                fontSize="small"
                                sx={{
                                    color: (theme) =>
                                        themeVariant === 'vibrant' ? '#ffffff' : theme.palette.primary.main,
                                }}
                            />
                        }
                        label={`${sortedData.folders.length} folders`}
                        color="primary"
                        variant={themeVariant === 'vibrant' ? 'filled' : 'outlined'}
                        size="small"
                        sx={{
                            fontWeight: 600,
                            backgroundColor: (theme) =>
                                themeVariant === 'vibrant'
                                    ? theme.palette.mode === 'dark'
                                        ? alpha(theme.palette.primary.main, 0.75)
                                        : alpha(theme.palette.primary.main, 0.75)
                                    : 'transparent',
                            color: (theme) =>
                                themeVariant === 'vibrant'
                                    ? theme.palette.mode === 'dark'
                                        ? '#ffffff'
                                        : '#ffffff'
                                    : theme.palette.primary.dark,
                            borderColor: (theme) =>
                                themeVariant === 'vibrant' ? theme.palette.primary.main : undefined,
                            borderWidth: themeVariant === 'vibrant' ? 2 : 1,
                            '& .MuiChip-icon': {
                                color: (theme) =>
                                    themeVariant === 'vibrant'
                                        ? '#ffffff'
                                        : undefined,
                            },
                        }}
                    />
                    <Chip
                        icon={
                            <VideoFileIcon
                                fontSize="small"
                                sx={{
                                    color: (theme) =>
                                        themeVariant === 'vibrant' ? '#ffffff' : theme.palette.secondary.main,
                                }}
                            />
                        }
                        label={`${sortedData.videoFiles.length} files`}
                        color="secondary"
                        variant={themeVariant === 'vibrant' ? 'filled' : 'outlined'}
                        size="small"
                        sx={{
                            fontWeight: 600,
                            backgroundColor: (theme) =>
                                themeVariant === 'vibrant'
                                    ? theme.palette.mode === 'dark'
                                        ? alpha(theme.palette.secondary.main, 0.75)
                                        : alpha(theme.palette.secondary.main, 0.75)
                                    : 'transparent',
                            color: (theme) =>
                                themeVariant === 'vibrant'
                                    ? theme.palette.mode === 'dark'
                                        ? '#ffffff'
                                        : '#ffffff'
                                    : theme.palette.primary.dark,
                            borderColor: (theme) =>
                                themeVariant === 'vibrant' ? theme.palette.secondary.main : undefined,
                            borderWidth: themeVariant === 'vibrant' ? 2 : 1,
                            '& .MuiChip-icon': {
                                color: (theme) =>
                                    themeVariant === 'vibrant'
                                        ? '#ffffff'
                                        : undefined,
                            },
                        }}
                    />
                    <Chip
                        icon={
                            <TextSnippetIcon
                                fontSize="small"
                                sx={{
                                    color: (theme) =>
                                        themeVariant === 'vibrant' ? '#ffffff' : theme.palette.info.main,
                                }}
                            />
                        }
                        label={`${sortedData.textFiles.length} files`}
                        color="info"
                        variant={themeVariant === 'vibrant' ? 'filled' : 'outlined'}
                        size="small"
                        sx={{
                            fontWeight: 600,
                            backgroundColor: (theme) =>
                                themeVariant === 'vibrant'
                                    ? theme.palette.mode === 'dark'
                                        ? alpha(theme.palette.info.main, 0.75)
                                        : alpha(theme.palette.info.main, 0.75)
                                    : 'transparent',
                            color: (theme) =>
                                themeVariant === 'vibrant'
                                    ? theme.palette.mode === 'dark'
                                        ? '#ffffff'
                                        : '#ffffff'
                                    : theme.palette.primary.dark,
                            borderColor: (theme) => (themeVariant === 'vibrant' ? theme.palette.info.main : undefined),
                            borderWidth: themeVariant === 'vibrant' ? 2 : 1,
                            '& .MuiChip-icon': {
                                color: (theme) =>
                                    themeVariant === 'vibrant'
                                        ? '#ffffff'
                                        : undefined,
                            },
                        }}
                    />
                    <Chip
                        icon={
                            <AudiotrackIcon
                                fontSize="small"
                                sx={{
                                    color: (theme) =>
                                        themeVariant === 'vibrant' ? '#ffffff' : theme.palette.warning.main,
                                }}
                            />
                        }
                        label={`${sortedData.audioFiles.length} files`}
                        color="warning"
                        variant={themeVariant === 'vibrant' ? 'filled' : 'outlined'}
                        size="small"
                        sx={{
                            fontWeight: 600,
                            backgroundColor: (theme) =>
                                themeVariant === 'vibrant'
                                    ? alpha(theme.palette.warning.main, 0.75)
                                    : 'transparent',
                            color: (theme) =>
                                themeVariant === 'vibrant' ? '#ffffff' : theme.palette.warning.dark,
                            borderColor: (theme) =>
                                themeVariant === 'vibrant' ? theme.palette.warning.main : undefined,
                            borderWidth: themeVariant === 'vibrant' ? 2 : 1,
                            '& .MuiChip-icon': {
                                color: (theme) =>
                                    themeVariant === 'vibrant' ? '#ffffff' : theme.palette.warning.main,
                            },
                        }}
                    />
                </Box>
            )}

            {/* Bulk selection action bar */}
            {isSelectMode && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        px: 2,
                        py: 1,
                        mb: 1,
                        borderRadius: 1,
                        backgroundColor: (theme) =>
                            selectPurpose === 'track'
                                ? alpha(theme.palette.primary.main, 0.08)
                                : theme.palette.mode === 'dark'
                                ? 'rgba(211, 47, 47, 0.15)'
                                : 'rgba(211, 47, 47, 0.08)',
                        border: '1px solid',
                        borderColor: selectPurpose === 'track' ? 'primary.light' : 'error.light',
                    }}
                >
                    <Typography variant="body2" sx={{ flex: 1, color: 'text.primary' }}>
                        {selectedFiles.size === 0
                            ? 'Click files to select them'
                            : `${selectedFiles.size} file${selectedFiles.size > 1 ? 's' : ''} selected`}
                    </Typography>
                    {selectedFiles.size > 0 && (
                        <Button
                            size="small"
                            variant="outlined"
                            color="inherit"
                            startIcon={<ClearIcon />}
                            onClick={() => setSelectedFiles(new Map())}
                        >
                            Clear
                        </Button>
                    )}
                    <Button
                        size="small"
                        variant="outlined"
                        color={selectPurpose === 'track' ? 'primary' : 'error'}
                        onClick={() => handleToggleSelectMode(selectPurpose)}
                    >
                        Cancel
                    </Button>
                    {selectedFiles.size > 0 && selectPurpose === 'track' && (
                        <Button size="small" variant="contained" onClick={() => setStartTrackingOpen(true)}>
                            Start tracking
                        </Button>
                    )}
                    {selectedFiles.size > 0 && selectPurpose !== 'track' && (
                        <Button
                            size="small"
                            variant="contained"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={handleBulkDeleteClick}
                        >
                            Delete Selected
                        </Button>
                    )}
                </Box>
            )}

            <Box
                className="file-grid"
                sx={{
                    borderRadius: 1,
                    overflow: 'hidden',
                    p: { xs: 1, sm: 2 },
                }}
            >
                {viewMode === 'assigned' ? (
                    loadingAssignments ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                            <CircularProgress />
                        </Box>
                    ) : assignedVideos.length === 0 ? (
                        <EmptyState
                            icon={<AssignmentIcon sx={{ fontSize: 60 }} />}
                            title="No Assignments"
                            message="You don't have any videos assigned to you."
                        />
                    ) : (
                        <>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                                Videos Assigned to You
                            </Typography>
                            <Grid container spacing={2}>
                                {assignedVideos.map((video) => (
                                    <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={video.id}>
                                        <FileGridItem
                                            file={video}
                                            onFileClick={onVideoFileClick}
                                            onMenuOpen={onMenuOpen}
                                            showPath={true}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </>
                    )
                ) : (
                    <ListView
                        folders={sortedData.folders}
                        videoFiles={sortedData.videoFiles}
                        textFiles={sortedData.textFiles}
                        audioFiles={sortedData.audioFiles}
                        bookFiles={sortedData.bookFiles}
                        onFolderClick={onFolderClick}
                        onVideoFileClick={onVideoFileClick}
                        onTextFileClick={onTextFileClick}
                        onAudioFileClick={onAudioFileClick}
                        onBookFileClick={onBookFileClick}
                        onMenuOpen={onMenuOpen}
                        showPaths={!!searchResults}
                        isSelectMode={isSelectMode}
                        selectedFiles={selectedFiles}
                        onSelectToggle={handleSelectToggle}
                    />
                )}
            </Box>

            <StartTrackingDialog
                open={startTrackingOpen}
                onClose={() => setStartTrackingOpen(false)}
                files={Array.from(selectedFiles.values())}
                onDone={() => {
                    setSelectedFiles(new Map());
                    setIsSelectMode(false);
                }}
            />

            {/* Text File Dialog */}
            <CreateTextFileDialog
                open={textFileDialogOpen}
                onClose={() => setTextFileDialogOpen(false)}
                currentPath={currentPath}
                onFileCreated={handleTextFileCreated}
            />

            {/* Book (IDML) Dialog */}
            <CreateBookDialog
                open={bookDialogOpen}
                onClose={() => setBookDialogOpen(false)}
                currentPath={currentPath}
                onFileCreated={handleBookFileCreated}
                darkMode={darkMode}
                themeVariant={themeVariant}
            />

            {/* Bulk Download Dialog */}
            <BulkDownloadDialog
                open={bulkDownloadDialogOpen}
                onClose={() => setBulkDownloadDialogOpen(false)}
                videoFiles={videoFiles}
                folderName={currentPath.split('/').pop() || 'Root'}
                currentPath={currentPath}
            />

            {/* Merge SRT Dialog */}
            <MergeSrtDialog
                open={mergeSrtDialogOpen}
                onClose={() => setMergeSrtDialogOpen(false)}
                videoFiles={videoFiles}
                folderName={currentPath.split('/').pop() || 'Root'}
                currentPath={currentPath}
            />

            {/* SRT Search Dialog */}
            <SrtSearchDialog
                open={srtSearchDialogOpen}
                onClose={() => setSrtSearchDialogOpen(false)}
                currentPath={currentPath}
                onResultClick={(result) => {
                    setSrtSearchDialogOpen(false);
                    onSrtSearchResultClick && onSrtSearchResultClick(result);
                }}
            />

        </Box>
    );
};

export default ModernFolderView;
